/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Media Studio & Generation Jobs DTO Schemas (OpenAPI 3.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';
import { AiJobStatus } from '../enums';

/**
 * Request payload for queuing an asynchronous AI image generation job.
 */
export const CreateImageJobSchema = z
  .object({
    prompt: z
      .string()
      .min(1, 'Prompt cannot be empty')
      .max(2000, 'Prompt must not exceed 2000 characters')
      .openapi({
        description: 'Text description of the image to generate',
        example: 'A futuristic cybernetic tiger in a neon Tokyo alley, cinematic lighting, 8k resolution',
      }),
    negativePrompt: z
      .string()
      .max(1000, 'Negative prompt must not exceed 1000 characters')
      .optional()
      .openapi({
        description: 'Negative prompt specifying visual elements to avoid',
        example: 'blurry, low quality, distorted, extra limbs, bad anatomy',
      }),
    model: z
      .string()
      .default('flux-pro')
      .openapi({
        description: 'Target AI image generation model identifier',
        example: 'flux-pro',
      }),
    aspectRatio: z
      .enum(['1:1', '16:9', '9:16', '4:3', '3:2'])
      .default('1:1')
      .openapi({
        description: 'Desired image aspect ratio',
        example: '1:1',
      }),
    width: z
      .number()
      .int()
      .min(256)
      .max(2048)
      .optional()
      .openapi({
        description: 'Explicit image width in pixels (optional)',
        example: 1024,
      }),
    height: z
      .number()
      .int()
      .min(256)
      .max(2048)
      .optional()
      .openapi({
        description: 'Explicit image height in pixels (optional)',
        example: 1024,
      }),
    numImages: z
      .number()
      .int()
      .min(1)
      .max(4)
      .default(1)
      .openapi({
        description: 'Number of images to generate (1 to 4)',
        example: 1,
      }),
    style: z
      .string()
      .optional()
      .openapi({
        description: 'Aesthetic preset (photorealistic, anime, cinematic, 3d-render)',
        example: 'cinematic',
      }),
    webhookUrl: z
      .string()
      .url('Webhook URL must be a valid HTTP/HTTPS URL')
      .optional()
      .openapi({
        description: 'Optional webhook callback URL notified upon job completion',
        example: 'https://mysite.com/api/webhooks/media',
      }),
  })
  .openapi('CreateImageJobRequest');

export type CreateImageJobRequest = z.infer<typeof CreateImageJobSchema>;

/**
 * Request payload for queuing an AI video generation job (Text-to-Video & Image-to-Video).
 */
export const CreateVideoJobSchema = z
  .object({
    prompt: z
      .string()
      .min(1, 'Prompt cannot be empty')
      .max(2000, 'Prompt must not exceed 2000 characters')
      .openapi({
        description: 'Text prompt describing the desired video scene, action and motion',
        example: 'A majestic blue whale gliding gracefully through starry cosmic clouds, cinematic 4k, hyper-detailed',
      }),
    imageUrl: z
      .string()
      .url('Image URL must be a valid HTTP/HTTPS URL')
      .optional()
      .openapi({
        description: 'Source image URL for Image-to-Video generation (optional)',
        example: 'https://media.sophiaplatform.com/images/whale.png',
      }),
    model: z
      .string()
      .default('luma-dream-machine')
      .openapi({
        description: 'Video generation model identifier (e.g. luma-dream-machine, kling-v1, runway-gen3)',
        example: 'luma-dream-machine',
      }),
    durationSeconds: z
      .number()
      .int()
      .min(3)
      .max(10)
      .default(5)
      .openapi({
        description: 'Video duration in seconds (typically 5 or 10)',
        example: 5,
      }),
    aspectRatio: z
      .enum(['16:9', '9:16', '1:1'])
      .default('16:9')
      .openapi({
        description: 'Target video aspect ratio',
        example: '16:9',
      }),
    loop: z
      .boolean()
      .default(false)
      .openapi({
        description: 'Whether the generated video should seamlessly loop',
        example: false,
      }),
    motion: z
      .number()
      .min(1)
      .max(10)
      .optional()
      .openapi({
        description: 'Camera motion intensity level (1 to 10)',
        example: 5,
      }),
    webhookUrl: z
      .string()
      .url('Webhook URL must be a valid HTTP/HTTPS URL')
      .optional()
      .openapi({
        description: 'Optional webhook callback URL notified upon video completion',
        example: 'https://mysite.com/api/webhooks/video',
      }),
  })
  .openapi('CreateVideoJobRequest');

export type CreateVideoJobRequest = z.infer<typeof CreateVideoJobSchema>;

/**
 * Request payload for queuing a Synthesia AI Talking Avatar video generation job.
 */
export const CreateAvatarVideoSchema = z
  .object({
    script: z
      .string()
      .min(1, 'Script text cannot be empty')
      .max(5000, 'Script text must not exceed 5000 characters')
      .openapi({
        description: 'Script or dialogue for the talking avatar to speak',
        example: 'Welcome to Sophia Platform, the next-generation AI operating system built entirely on Cloudflare Workers.',
      }),
    avatarId: z
      .string()
      .min(1, 'Avatar ID cannot be empty')
      .default('anna_costume1_cameraA')
      .openapi({
        description: 'Synthesia avatar model identifier',
        example: 'anna_costume1_cameraA',
      }),
    voiceId: z
      .string()
      .min(1, 'Voice ID cannot be empty')
      .default('en_us_001')
      .openapi({
        description: 'Synthesia or ElevenLabs TTS voice identifier',
        example: 'en_us_001',
      }),
    background: z
      .enum(['transparent', 'green_screen', 'office', 'studio', 'custom'])
      .default('transparent')
      .openapi({
        description: 'Avatar video background preset or transparent background',
        example: 'transparent',
      }),
    customBackgroundUrl: z
      .string()
      .url('Custom background URL must be a valid HTTP/HTTPS URL')
      .optional()
      .openapi({
        description: 'Custom background image or video URL if background is custom',
        example: 'https://media.sophiaplatform.com/backgrounds/modern-office.jpg',
      }),
    model: z
      .string()
      .default('synthesia')
      .openapi({
        description: 'AI avatar generation provider code',
        example: 'synthesia',
      }),
    aspectRatio: z
      .enum(['16:9', '9:16', '1:1'])
      .default('16:9')
      .openapi({
        description: 'Video aspect ratio',
        example: '16:9',
      }),
    title: z
      .string()
      .max(200)
      .optional()
      .openapi({
        description: 'Title for the generated avatar video',
        example: 'Sophia Platform Introduction',
      }),
    webhookUrl: z
      .string()
      .url('Webhook URL must be a valid HTTP/HTTPS URL')
      .optional()
      .openapi({
        description: 'Optional webhook callback URL notified upon completion',
        example: 'https://mysite.com/api/webhooks/avatar-video',
      }),
  })
  .openapi('CreateAvatarVideoRequest');

export type CreateAvatarVideoRequest = z.infer<typeof CreateAvatarVideoSchema>;

/**
 * Immediate response upon queuing a background media job (HTTP 202 Accepted).
 */
export const CreateJobResponseSchema = z
  .object({
    jobId: z.string().uuid().openapi({
      description: 'Unique background generation job identifier',
      example: '9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d',
    }),
    status: z.nativeEnum(AiJobStatus).openapi({
      description: 'Initial job queue status',
      example: AiJobStatus.QUEUED,
    }),
    creditsCost: z.number().openapi({
      description: 'Credits deducted/held for this operation',
      example: 5,
    }),
    estimatedSeconds: z.number().int().openapi({
      description: 'Estimated wait time in seconds until completion',
      example: 15,
    }),
  })
  .openapi('CreateJobResponse');

export type CreateJobResponse = z.infer<typeof CreateJobResponseSchema>;

/**
 * Full details of a background generation job for polling.
 */
export const GenerationJobDetailSchema = z
  .object({
    id: z.string().uuid().openapi({
      description: 'Job UUID',
      example: '9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d',
    }),
    workspaceId: z.string().openapi({
      description: 'Workspace ID owning the job',
      example: 'ws_01HXYZ123',
    }),
    jobType: z.string().openapi({
      description: 'Type of generation job',
      example: 'image_gen',
    }),
    status: z.nativeEnum(AiJobStatus).openapi({
      description: 'Current execution status',
      example: AiJobStatus.COMPLETED,
    }),
    progressPercent: z.number().int().min(0).max(100).openapi({
      description: 'Job execution progress (0-100%)',
      example: 100,
    }),
    payload: z.record(z.unknown()).openapi({
      description: 'Original input parameters and options for the job',
    }),
    result: z.record(z.unknown()).nullable().optional().openapi({
      description: 'Output assets and generation metadata if completed',
    }),
    errorMessage: z.string().nullable().optional().openapi({
      description: 'Error description if job failed',
      example: null,
    }),
    webhookUrl: z.string().nullable().optional().openapi({
      description: 'Callback webhook URL if configured',
      example: null,
    }),
    createdAt: z.string().or(z.number()).openapi({
      description: 'Job creation timestamp',
    }),
    completedAt: z.string().or(z.number()).nullable().optional().openapi({
      description: 'Job completion timestamp',
    }),
  })
  .openapi('GenerationJobDetail');

export type GenerationJobDetail = z.infer<typeof GenerationJobDetailSchema>;

/**
 * Request payload for queuing an AI Inpainting / Object Replacement job (Task 6.4.1).
 */
export const InpaintImageSchema = z
  .object({
    image: z
      .string()
      .min(1, 'Source image URL or Base64 is required')
      .openapi({
        description: 'Source image URL or Base64 data URI to inpaint',
        example: 'https://media.sophiaplatform.com/images/source.png',
      }),
    mask: z
      .string()
      .optional()
      .openapi({
        description: 'Optional mask image URL or Base64 (white pixels indicate inpaint area)',
        example: 'https://media.sophiaplatform.com/images/mask.png',
      }),
    prompt: z
      .string()
      .min(1, 'Prompt describing the inpainting modification is required')
      .max(2000, 'Prompt must not exceed 2000 characters')
      .openapi({
        description: 'Text description of what should appear inside the inpainted mask area',
        example: 'A vintage red leather armchair with gold embroidery',
      }),
    negativePrompt: z
      .string()
      .max(1000)
      .optional()
      .openapi({
        description: 'Elements to avoid within the inpainted area',
        example: 'blurry, distorted, mismatched lighting',
      }),
    model: z
      .string()
      .default('stability-inpaint')
      .openapi({
        description: 'Inpainting AI model identifier (e.g. stability-inpaint, flux-inpaint)',
        example: 'stability-inpaint',
      }),
    outputFormat: z
      .enum(['png', 'webp', 'jpeg'])
      .default('png')
      .openapi({
        description: 'Output image format',
        example: 'png',
      }),
    growMask: z
      .number()
      .int()
      .min(0)
      .max(100)
      .optional()
      .openapi({
        description: 'Number of pixels to expand the mask border (feathering effect)',
        example: 5,
      }),
    webhookUrl: z
      .string()
      .url('Webhook URL must be a valid HTTP/HTTPS URL')
      .optional()
      .openapi({
        description: 'Optional webhook callback URL notified upon job completion',
        example: 'https://mysite.com/api/webhooks/inpaint',
      }),
  })
  .openapi('InpaintImageRequest');

export type InpaintImageRequest = z.infer<typeof InpaintImageSchema>;

/**
 * Request payload for queuing an AI Super-Resolution Upscaling job (Task 6.4.2).
 */
export const UpscaleImageSchema = z
  .object({
    image: z
      .string()
      .min(1, 'Source image URL or Base64 is required')
      .openapi({
        description: 'Source image URL or Base64 data URI to upscale',
        example: 'https://media.sophiaplatform.com/images/lowres.png',
      }),
    scale: z
      .enum(['2', '4', '2x', '4x'])
      .default('2')
      .openapi({
        description: 'Upscale magnification factor (2x or 4x resolution)',
        example: '2',
      }),
    model: z
      .string()
      .default('real-esrgan')
      .openapi({
        description: 'Upscaler model identifier (e.g. real-esrgan, clarity-upscaler, stability-upscale)',
        example: 'real-esrgan',
      }),
    creativity: z
      .number()
      .min(0)
      .max(1)
      .optional()
      .openapi({
        description: 'Level of creative hallucination / detail synthesis (0.0 to 1.0)',
        example: 0.35,
      }),
    outputFormat: z
      .enum(['png', 'webp', 'jpeg'])
      .default('png')
      .openapi({
        description: 'Output image format',
        example: 'png',
      }),
    webhookUrl: z
      .string()
      .url('Webhook URL must be a valid HTTP/HTTPS URL')
      .optional()
      .openapi({
        description: 'Optional webhook callback URL notified upon job completion',
        example: 'https://mysite.com/api/webhooks/upscale',
      }),
  })
  .openapi('UpscaleImageRequest');

export type UpscaleImageRequest = z.infer<typeof UpscaleImageSchema>;

/**
 * Request payload for queuing an AI Background Removal job (Task 6.4.3).
 */
export const RemoveBackgroundSchema = z
  .object({
    image: z
      .string()
      .min(1, 'Source image URL or Base64 is required')
      .openapi({
        description: 'Source image URL or Base64 data URI from which to remove background',
        example: 'https://media.sophiaplatform.com/images/portrait.jpg',
      }),
    model: z
      .string()
      .default('birefnet')
      .openapi({
        description: 'Background removal model identifier (e.g. birefnet, rmbg, stability-remove-bg)',
        example: 'birefnet',
      }),
    outputFormat: z
      .enum(['png', 'webp'])
      .default('png')
      .openapi({
        description: 'Output format (PNG or WebP for transparent alpha channel support)',
        example: 'png',
      }),
    webhookUrl: z
      .string()
      .url('Webhook URL must be a valid HTTP/HTTPS URL')
      .optional()
      .openapi({
        description: 'Optional webhook callback URL notified upon job completion',
        example: 'https://mysite.com/api/webhooks/remove-bg',
      }),
  })
  .openapi('RemoveBackgroundRequest');

export type RemoveBackgroundRequest = z.infer<typeof RemoveBackgroundSchema>;

/**
 * Query schema for listing generation jobs with pagination and filters.
 */
export const ListGenerationJobsQuerySchema = z
  .object({
    type: z
      .enum([
        'image_gen',
        'video_gen',
        'image_inpaint',
        'image_upscale',
        'image_remove_bg',
        'whisper_stt',
        'tts',
      ])
      .optional()
      .openapi({
        description: 'Filter by job type',
        example: 'image_gen',
      }),
    status: z.nativeEnum(AiJobStatus).optional().openapi({
      description: 'Filter by status (queued, processing, completed, failed)',
      example: AiJobStatus.COMPLETED,
    }),
    limit: z.coerce.number().int().min(1).max(100).default(20).openapi({
      description: 'Number of items per page',
      example: 20,
    }),
    offset: z.coerce.number().int().min(0).default(0).openapi({
      description: 'Offset items for pagination',
      example: 0,
    }),
  })
  .openapi('ListGenerationJobsQuery');

export type ListGenerationJobsQuery = z.infer<typeof ListGenerationJobsQuerySchema>;

/**
 * Message payload pushed to Cloudflare Queues for processing.
 */
export interface GenerationJobQueueMessage {
  jobId: string;
  workspaceId: string;
  userId: string;
  jobType:
    | 'image_gen'
    | 'video_gen'
    | 'image_inpaint'
    | 'image_upscale'
    | 'image_remove_bg'
    | 'whisper_stt'
    | 'tts';
  model: string;
  payload: Record<string, unknown>;
  creditsCost: number;
  createdAt: number;
}

/**
 * Media asset response representation (Task 6.5.3).
 */
export const MediaAssetResponseSchema = z
  .object({
    id: z.string().openapi({
      description: 'Unique media asset UUID identifier',
      example: 'a0eebc99-9c0b-4ef8-bb6d-6bb9bd380a11',
    }),
    workspaceId: z.string().openapi({
      description: 'Associated workspace identifier',
      example: 'ws_01HXYZ123',
    }),
    userId: z.string().openapi({
      description: 'Identifier of the user who generated or uploaded the asset',
      example: 'user_01HXYZ456',
    }),
    assetType: z
      .enum(['image', 'video', 'audio_tts', 'audio_stt'])
      .openapi({
        description: 'Categorical type of the media asset',
        example: 'image',
      }),
    r2Key: z.string().openapi({
      description: 'Cloudflare R2 object storage key',
      example: 'images/ws_01HXYZ123/a0eebc99-9c0b-4ef8-bb6d-6bb9bd380a11.png',
    }),
    publicUrl: z.string().openapi({
      description: 'Public zero-egress CDN URL delivering the asset',
      example: 'https://media.sophiaplatform.com/images/ws_01HXYZ123/a0eebc99-9c0b-4ef8-bb6d-6bb9bd380a11.png',
    }),
    mimeType: z.string().openapi({
      description: 'Standard MIME content type of the media file',
      example: 'image/png',
    }),
    fileSizeBytes: z.number().openapi({
      description: 'Total file size in bytes',
      example: 1048576,
    }),
    prompt: z.string().nullable().optional().openapi({
      description: 'Original text prompt used to generate the media asset',
      example: 'A cybernetic neon tiger in Tokyo',
    }),
    generatorCode: z.string().nullable().optional().openapi({
      description: 'AI model / engine generator code',
      example: 'flux-pro',
    }),
    metadata: z.record(z.unknown()).optional().openapi({
      description: 'Additional asset metadata (dimensions, duration, seed, etc.)',
      example: { width: 1024, height: 1024, style: 'cinematic' },
    }),
    creditsCost: z.number().openapi({
      description: 'Number of credits consumed to create this asset',
      example: 5,
    }),
    createdAt: z.union([z.string(), z.number()]).openapi({
      description: 'Asset creation timestamp (ISO-8601 or UNIX epoch)',
      example: '2026-10-20T09:00:00.000Z',
    }),
  })
  .openapi('MediaAssetResponse');

export type MediaAssetResponse = z.infer<typeof MediaAssetResponseSchema>;

/**
 * Query schema for filtering and paginating media gallery assets (Task 6.5.3).
 */
export const ListMediaAssetsQuerySchema = z
  .object({
    assetType: z
      .enum(['image', 'video', 'audio_tts', 'audio_stt'])
      .optional()
      .openapi({
        description: 'Filter assets by media category',
        example: 'image',
      }),
    generatorCode: z
      .string()
      .optional()
      .openapi({
        description: 'Filter by generator model code (e.g. flux-pro, dall-e-3, luma-dream-machine)',
        example: 'flux-pro',
      }),
    search: z
      .string()
      .optional()
      .openapi({
        description: 'Search string matched against original prompt text',
        example: 'tiger',
      }),
    limit: z.coerce.number().int().min(1).max(100).default(20).openapi({
      description: 'Maximum items to return per page',
      example: 20,
    }),
    offset: z.coerce.number().int().min(0).default(0).openapi({
      description: 'Offset items for pagination',
      example: 0,
    }),
  })
  .openapi('ListMediaAssetsQuery');

export type ListMediaAssetsQuery = z.infer<typeof ListMediaAssetsQuerySchema>;

/**
 * Response schema for successful asset deletion (Task 6.5.3).
 */
export const DeleteMediaAssetResponseSchema = z
  .object({
    success: z.boolean().openapi({
      description: 'Whether the deletion operation succeeded',
      example: true,
    }),
    message: z.string().openapi({
      description: 'Human-readable confirmation message',
      example: 'Media asset deleted successfully',
    }),
    deletedId: z.string().openapi({
      description: 'Identifier of the deleted media asset',
      example: 'a0eebc99-9c0b-4ef8-bb6d-6bb9bd380a11',
    }),
  })
  .openapi('DeleteMediaAssetResponse');

export type DeleteMediaAssetResponse = z.infer<typeof DeleteMediaAssetResponseSchema>;
